/**
 * OP-pinned classification for a reporter comparison entry.
 *
 * Cortex emits a per-finding recommended threshold (`ai.recommended_threshold`)
 * that pins the OP this finding's classification reflects. Strict — surface a
 * validation error if the field is missing or unrecognized so a data-shape
 * regression is loud (was a silent fall-back to `high_specificity` before).
 *
 * This module is the single source of truth for that no-fallback rule; both
 * `ComparisonPanel` and the surfacer retro stage consume it. See
 * web-demo-frontend/CLAUDE.md "No silent OP fallback in classification".
 */
import type {
  ComparisonClassification,
  ComparisonEntry,
} from "../hooks/useReporterComparisonQueries";

export type ActiveClassification = "agreement" | "ai_only" | "report_only";

const OP_KEYS = new Set<keyof ComparisonClassification>([
  "high_sensitivity",
  "sensitivity_biased",
  "balanced",
  "high_specificity",
]);

export function isOpKey(value: string | undefined): value is keyof ComparisonClassification {
  return OP_KEYS.has(value as keyof ComparisonClassification);
}

// Returns an Error sentinel rather than throwing because callers run this inside
// useMemo: a throw mid-render aborts the hook list and React reports it as
// "rendered fewer hooks than expected" instead of the actual message.
export function getClassification(entry: ComparisonEntry): ActiveClassification | null | Error {
  if (!isOpKey(entry.ai.recommended_threshold)) {
    return new Error(
      `Finding missing or invalid ai.recommended_threshold ` +
        `(finding=${entry.finding}, threshold=${entry.ai?.recommended_threshold ?? "undefined"})`,
    );
  }
  const cls = entry.classification[entry.ai.recommended_threshold];
  if (cls === "neither") return null;
  return cls;
}
